const apiUrl: string = process.env.REACT_APP_BACKEND_API_URL || process.env.REACT_APP_API_URL!

export interface CustomerInvoice {
	documentType?: 'credit_note'
	id: string
	invoiceNumber: string
	currency: string
	taxTreatment: string
	issuedDate: string
}
export async function fetchOrderInvoices(orderNumber: string, token: string, signal?: AbortSignal): Promise<{ invoices: CustomerInvoice[]; generationStatus: string } | CustomerInvoice[]> {
	const response = await fetch(`${apiUrl}/tracking/${encodeURIComponent(orderNumber)}/invoices/`, {
		headers: { 'X-Kibu-Tracking-Token': token }, cache: 'no-store', signal,
	})
	if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? 'Verify your order again to access invoices.' : 'Invoices could not be loaded. Please try again.')
	return await response.json()
}
export async function downloadOrderInvoice(orderNumber: string, invoice: CustomerInvoice, token: string, signal?: AbortSignal) {
	const response = await fetch(`${apiUrl}/tracking/${encodeURIComponent(orderNumber)}/invoices/${encodeURIComponent(invoice.id)}/pdf/`, {
		headers: { 'X-Kibu-Tracking-Token': token }, cache: 'no-store', signal,
	})
	if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? 'Verify your order again to access invoices.' : 'The invoice could not be downloaded. Please try again.')
	const blob = await response.blob()
	if (signal?.aborted) return
	const url = URL.createObjectURL(blob)
	const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${invoice.invoiceNumber}.pdf`; anchor.click()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export interface StatusHistoryEntry {
	status: OrderStatus
	timestamp: string
	note: string
}

export interface ShippingInfo {
	carrier: string
	tracking_number: string
	tracking_url: string
	shipped_at: string
	service_level: string
}

export interface PublicTrackingResponse {
	object: 'tracking'
	order_number: string
	status: OrderStatus
	created_at: string
	status_history: StatusHistoryEntry[]
	shipping?: ShippingInfo
	estimated_delivery?: string
	delay?: {
		type: string
		days: number
		updated_at: string
	}
}

export interface CustomerDetails {
	name: string
	email: string
	phone: string
	address: {
		city: string
		country: string
		line1: string
		line2: string | null
		postal_code: string
		state: string | null
	}
}

export interface OrderItem {
	productItemId: string
	quantity: number
	paymentValue: number
	paymentCurrency: string
	productName?: string
	optionTitle?: string
	imageUrl?: string
	customColourPreview?: {status: 'ready' | 'unavailable'; layers?: Array<{url: string; index: number}>; bounds?: [number, number, number, number] | null}
	productType?: string
	componentModifications?: Record<string, unknown>
	components?: unknown[]
	parentProductItemId?: string
	enrichedComponents?: Array<{ name: string; material: string; side: string }>
}

export interface PaymentDetails {
 amountDiscount?: number
	amountTotal: number
	amountSubtotal: number
	amountShipping: number
	currency: string
	paymentStatus: string
}

export interface ShippingDetails {
	name: string
	address: {
		city: string
		country: string
		line1: string
		line2: string | null
		postal_code: string
		state: string | null
	}
}

export interface ShippingTracking {
	trackingNumber: string
	carrierCode: string
	carrierName: string
	carrierTrackingUrl: string
	shippingCost: number
	insurance: boolean
	serviceLevel: string
	shippedAt: string
}

export interface TrackingUpgradeConfiguration {
 configured: boolean
 offers: Array<{ id: string; kind: 'care'; title: string; description: string; buttonLabel: string; cutoff: string; cutoffLabel?: string; imageUrl?: string }>
}

export interface DetailedOrder {
 postPurchaseUpgrades?: TrackingUpgradeConfiguration

	_id: string
	orderNumber: string
	checkoutSessionId: string
	status: OrderStatus
	orderType?: 'standard' | 'repair'
	customerDetails: CustomerDetails
	items: OrderItem[]
	paymentDetails: PaymentDetails
	shippingDetails: ShippingDetails
	shippingTracking: ShippingTracking
	createdAt: string
	updatedAt: string
	marketingAttribution?: {
		purchaseEventId?: string
	}
}

export interface DetailedTrackingResponse {
	object: 'order_tracking_detailed'
	order_number: string
	order: DetailedOrder
}

export interface TokenRequestResponse {
	success: boolean
	message: string
}

export interface TokenInfoResponse {
	valid: boolean
	order_number?: string
	expires_at?: string
	issued_at?: string
	message?: string
	revoked?: boolean
}

export type OrderStatus =
	| 'pending'
	| 'paid'
	| 'repair_request'
	| 'processed'
	| 'parts_collected'
	| 'production'
	| 'production_complete'
	| 'packed'
	| 'shipped'
	| 'delivered'
	| 'cancelled'
	| 'delay'
	| 'delay_cleared'
	| 'delayed'

// Token storage helpers
const TOKEN_STORAGE_PREFIX = 'kibu_order_token_'

export const storeOrderToken = (orderNumber: string, token: string): void => {
	try {
		localStorage.setItem(`${TOKEN_STORAGE_PREFIX}${orderNumber}`, token)
	} catch (error) {
		console.error('Failed to store order token:', error)
	}
}

export const getStoredOrderToken = (orderNumber: string): string | null => {
	try {
		return localStorage.getItem(`${TOKEN_STORAGE_PREFIX}${orderNumber}`)
	} catch (error) {
		console.error('Failed to get stored order token:', error)
		return null
	}
}

export const removeStoredOrderToken = (orderNumber: string): void => {
	try {
		localStorage.removeItem(`${TOKEN_STORAGE_PREFIX}${orderNumber}`)
	} catch (error) {
		console.error('Failed to remove stored order token:', error)
	}
}

// API functions
export class TrackingRequestError extends Error {
	constructor(message: string, public status = 0, public kind: 'http' | 'timeout' | 'network' | 'invalid' = 'http') {
		super(message)
		this.name = 'TrackingRequestError'
	}
}

interface TrackingRequestOptions {
	signal?: AbortSignal
	timeoutMs?: number
}

// Bound the entire response, including its body, rather than leaving a customer
// on a spinner when a connection or proxy stalls.
async function trackingRequest<T>(path: string, options: TrackingRequestOptions): Promise<T> {
	const controller = new AbortController()
	const cancel = () => controller.abort()
	if (options.signal?.aborted) controller.abort()
	options.signal?.addEventListener('abort', cancel, { once: true })
	let timer: ReturnType<typeof setTimeout> | undefined
	try {
		const request = (async () => {
			const response = await fetch(`${apiUrl}${path}`, { signal: controller.signal })
			let data
			try { data = await response.json() } catch {
				if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
				throw new TrackingRequestError('We couldn’t read the order update. Please try again.', response.status, 'invalid')
			}
			if (!response.ok) throw new TrackingRequestError(
				response.status === 404 ? 'We couldn’t find that order number. Please check your confirmation email.' : 'We couldn’t load the order update. Please try again.',
				response.status,
			)
			if (!data || typeof data !== 'object' || Array.isArray(data)) throw new TrackingRequestError('We couldn’t read the order update. Please try again.', 0, 'invalid')
			return data as T
		})()
		const deadline = new Promise<never>((_, reject) => {
			timer = setTimeout(() => {
				reject(new TrackingRequestError('The order update is taking longer than expected. Please try again.', 0, 'timeout'))
				controller.abort()
			}, options.timeoutMs ?? 15000)
		})
		return await Promise.race([request, deadline])
	} catch (error) {
		if (options.signal?.aborted || error instanceof TrackingRequestError) throw error
		throw new TrackingRequestError('We couldn’t connect to your order update. Please try again.', 0, 'network')
	} finally {
		clearTimeout(timer)
		options.signal?.removeEventListener('abort', cancel)
	}
}

export const fetchPublicTracking = async (
	orderNumber: string,
	options: TrackingRequestOptions = {},
): Promise<PublicTrackingResponse> => {
	return trackingRequest(`/tracking/${encodeURIComponent(orderNumber)}/`, options)
}

export const fetchDetailedTracking = async (
	orderNumber: string,
	token: string,
	options: TrackingRequestOptions = {},
): Promise<DetailedTrackingResponse> => {
	return trackingRequest(`/tracking/${encodeURIComponent(orderNumber)}/detailed/?token=${encodeURIComponent(token)}`, options)
}

export const requestTrackingToken = async (
	orderNumber: string,
	email: string
): Promise<TokenRequestResponse> => {
	const response = await fetch(`${apiUrl}/tracking/request-token/`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: JSON.stringify({
			order_number: orderNumber,
			email: email
		})
	})

	if (!response.ok) {
		const error = await response.json()
		throw new Error(error.error?.message || 'Failed to request tracking token')
	}

	return response.json()
}

export const validateTrackingToken = async (token: string): Promise<TokenInfoResponse> => {
	const response = await fetch(`${apiUrl}/tracking/token-info/?token=${encodeURIComponent(token)}`)

	if (!response.ok) {
		throw new Error('Failed to validate token')
	}

	return response.json()
}

// --- Kibu Care upgrade / purchase ---

export interface KibuCareOption {
	planId: string
	planName: string
	optionTitle: string
	planYears: number
	priceAmount: number
	priceCurrency: string
	isUpgrade: boolean
	eligibleQuantity: number
	imageUrl?: string | null
}

export interface KibuCareCurrentPlan {
	planYears: number
	expiryDate: string
}

export interface KibuCareEligibilityResponse {
 checkoutMode?: 'unified'
 careOfferId?: string
	eligible: boolean
	ineligibleReason?: 'not_uk' | 'not_paid' | 'window_expired' | 'no_eligible_products' | string
	daysRemaining?: number
	orderedAt?: string
	shippedAt?: string | null
	currentPlan?: KibuCareCurrentPlan | null
	availableOptions?: KibuCareOption[]
}

export interface KibuCareSessionResponse {
	sessionUrl: string
}

export const fetchKibuCareEligibility = async (
	orderNumber: string,
	token: string
): Promise<KibuCareEligibilityResponse> => {
	const response = await fetch(
		`${apiUrl}/tracking/${orderNumber}/kibucare-upgrade/?token=${encodeURIComponent(token)}`
	)

	if (!response.ok) {
		const error = await response.json().catch(() => ({}))
		throw new Error(error?.error?.message || 'Failed to check Kibu Care eligibility')
	}

	return response.json()
}

export const createKibuCareSession = async (
	orderNumber: string,
	token: string,
	planId: string
): Promise<KibuCareSessionResponse> => {
	const response = await fetch(
		`${apiUrl}/tracking/${orderNumber}/kibucare-upgrade/?token=${encodeURIComponent(token)}`,
		{
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ plan_id: planId }),
		}
	)

	if (!response.ok) {
		const error = await response.json().catch(() => ({}))
		throw new Error(error?.error?.message || 'Failed to create checkout session')
	}

	return response.json()
}

export interface PhysicalUpgradeOffer {
 id: string; kind: 'replacement' | 'engraving'; title: string; description: string; buttonLabel: string;
 price: { amount: number; currency: string; country: string }; cutoffLabel: string; maxLength?: number;
 /** Milestone key; not sent by the backend yet, so cutoffLabel is the fallback. */
 cutoff?: string
 /** Card image chosen in the ERP asset library; optional until the backend sends it. */
 imageUrl?: string
 lines: Array<{ lineKey: string; lineIndex?: number; productItemId?: string; name: string; quantity: number; eligible: boolean }>
}
export interface UpgradeChoice { offerId: string; lineKey: string; unitIndex: number; engravingText?: string; planId?: string }
const upgradePath = (order: string, token: string, suffix = '') => `/tracking/${encodeURIComponent(order)}/order-upgrades/${suffix}?token=${encodeURIComponent(token)}`
export interface UpgradePurchase { purchaseId: string; status: string; canResume: boolean; blocksCheckout: boolean }
export const fetchOrderUpgradeOffers = (order: string, token: string) => trackingRequest<{ offers: PhysicalUpgradeOffer[]; purchase?: UpgradePurchase | null }>(upgradePath(order, token), {})
async function upgradeAction<T>(path: string, body: unknown): Promise<T> {
 const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 25000)
 try {
  const response = await fetch(`${apiUrl}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: controller.signal })
  const data = await response.json()
  if (!response.ok) throw new Error(data?.error?.message || 'We could not confirm the upgrade. Please retry the same selection.')
  return data as T
 } finally { clearTimeout(timeout) }
}
export const createOrderUpgradeCheckout = (order: string, token: string, requestKey: string, selections: UpgradeChoice[]) => upgradeAction<{ purchaseId: string; sessionUrl: string }>(upgradePath(order, token), { requestKey, selections })
export const fetchOrderUpgradePurchase = (order: string, token: string, purchase: string, cancel = false) => cancel
 ? upgradeAction<{ status: string }>(upgradePath(order, token, `${encodeURIComponent(purchase)}/`), { action: 'cancel' })
 : trackingRequest<{ status: string }>(upgradePath(order, token, `${encodeURIComponent(purchase)}/`), {})
export const resumeOrderUpgradeCheckout = (order: string, token: string, purchase: string) => upgradeAction<{ sessionUrl?: string; status?: string }>(upgradePath(order, token, `${encodeURIComponent(purchase)}/`), { action: 'resume' })
